<template>
  <div v-if="basicData && priceList">
    <!-- Table -->
    <div class="order-info" v-if="orderListOnProduction">
      <table class="order-info__table">
        <thead>
          <tr>
            <th>
              {{ $t("page.order.submit.price.table.header.description") }}
            </th>
            <th>{{ $t("page.order.submit.price.table.header.delivery") }}</th>
            <th>
              {{ $t("page.order.submit.price.table.header.estimateddate") }}
            </th>
            <th class="text-right" colspan="2">
              {{ $t("page.order.submit.price.table.header.price") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="element in onProductionElements" :key="'price-' + element.id">
            <td>
              <strong>{{ element.canisters }} x</strong>
              {{ $t("page.order.submit.price.table.text.canister") }}
            </td>
            <td>
              <strong>{{ $t(element.deliveryTypeTranslationKey) }}</strong>
              ({{ formatPrice(element.netPrice) }} / {{ $t("page.order.submit.price.table.text.canister") }})
            </td>
            <td>
              {{ formatDate(element.estimatedDeliveryDate, basicData.checkoutDisplayMode === "us") }}
            </td>
            <td class="text-right" :class="{ 'text-grey': freeContingent }" colspan="2">
              {{ element.totalPrice }}
            </td>
          </tr>
          <tr class="subtotal">
            <td colspan="3"></td>
            <td>{{ $t("page.order.submit.price.table.text.subtotal") }}</td>
            <td id="subtotal-price" class="text-right" :class="{ 'text-grey': freeContingent }">
              {{ formatPrice(netPrice) }}
            </td>
          </tr>
          <tr v-if="basicData.vatTaxRate > 0" class="subtotal subtotal--last">
            <td colspan="3"></td>
            <td>
              {{ $t("page.order.submit.price.table.text.vat.zzgl") }}
              {{ basicData.vatTaxRate.toFixed(2) }}%
              {{ $t("page.order.submit.price.table.text.vat.mwst") }}
            </td>
            <td class="text-right" :class="{ 'text-grey': freeContingent }">+ {{ formatPrice(vatPrice) }}</td>
          </tr>
          <tr class="intotal">
            <td colspan="3"></td>
            <td>{{ $t("page.order.submit.price.table.text.intotal") }}</td>
            <td class="text-right" id="total-price">
              <div v-if="freeContingent">
                <span class="text-grey text-free">
                  {{ formatPrice(parseFloat(netPrice) + parseFloat(vatPrice)) }}
                </span>
                {{ formatPrice(0) }}
              </div>
              <div v-else>
                {{ formatPrice(parseFloat(netPrice) + parseFloat(vatPrice)) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Table -->
    <div v-if="orderListOnHold && orderListOnHold.length > 0">
      <div class="order-info">
        <div class="popup-info" :class="{ open: tableTooltip }">
          <span class="popup-info__close" @click="hideTooltip">
            <img src="~@/assets/icons/icon-cancel-black.svg" />
          </span>
          <img src="~@/assets/icons/icon-table-popup.svg" />
          <div class="popup-info__text">
            <div class="popup-info__title">{{ $t("page.order.submit.rotorunderconstruction.popup.header") }}</div>
            <p class="popup-info__desc">
              {{ $t("page.order.submit.rotorunderconstruction.popup.description") }}
            </p>
          </div>
        </div>
        <table class="order-info__table">
          <thead>
            <tr>
              <th>
                {{ $t("page.order.submit.price.table.header.description") }}
              </th>
              <th>{{ $t("page.order.submit.price.table.header.delivery") }}</th>
              <th>
                {{ $t("page.order.submit.price.table.header.estimateddate") }}
              </th>
              <th class="text-right" colspan="2">
                {{ $t("page.order.submit.price.table.header.price") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="has-child">
              <td>
                <strong>{{ onHold.canisters }} x</strong>
                {{ $t("page.order.submit.price.table.text.canister") }}
              </td>
              <td>
                <strong>
                  {{ $t("page.order.submit.price.table.text.hold") }}
                </strong>
                {{ $t("page.order.submit.price.table.text.feasibilitycheck") }}
              </td>
              <td>
                <em>
                  {{ $t("page.order.submit.price.table.text.confirmation") }}
                </em>
                -
                {{ formatDate(onHold.estimatedOnHoldConfirmationDate, basicData.checkoutDisplayMode === "us") }}
              </td>
              <td class="text-right" colspan="2">
                {{ formatPrice(0) }}
              </td>
            </tr>
            <tr>
              <td></td>
              <td>
                <strong>
                  +
                  {{ $t("page.order.submit.price.table.text.standarddelivery") }}
                </strong>
                ({{ formatPrice(onHold.price.netPrice) }} / {{ $t("page.order.submit.price.table.text.canister") }})
              </td>
              <td>
                <em>
                  {{ $t("page.order.submit.price.table.text.ifapplicable") }}
                </em>
                -
                {{ formatDate(onHold.estimatedDeliveryDate, basicData.checkoutDisplayMode === "us") }}
              </td>
              <td class="text-right" colspan="2">
                {{ formatPrice(onHold.totalPrice) }}
              </td>
            </tr>
            <tr class="subtotal">
              <td colspan="3"></td>
              <td>{{ $t("page.order.submit.price.table.text.subtotal") }}</td>
              <td class="text-right">
                {{ formatPrice(onHold.totalPrice) }}
              </td>
            </tr>
            <tr v-if="basicData.vatTaxRate > 0" class="subtotal subtotal--last">
              <td colspan="3"></td>
              <td>
                {{ $t("page.order.submit.price.table.text.vat.zzgl") }}
                {{ basicData.vatTaxRate.toFixed(2) }}%
                {{ $t("page.order.submit.price.table.text.vat.mwst") }}
              </td>
              <td class="text-right">+ {{ formatPrice(onHold.vatPrice) }}</td>
            </tr>
            <tr class="intotal">
              <td colspan="3"></td>
              <td>{{ $t("page.order.submit.price.table.text.intotal") }}</td>
              <td class="text-right">
                <div>{{ formatPrice(parseFloat(onHold.totalPrice) + parseFloat(onHold.vatPrice)) }}*</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="order-info__desc">{{$t("page.order.info.feasbility.check")}}</div>
    </div>
  </div>
  <v-loader v-else />
</template>

<script>
import { mapGetters } from "vuex";
import { VLoader } from "shared";
import * as DateUtils from "@/utils/DateUtils";
import moment from "moment/moment";

export default {
  name: "OrderSubmitPrice",
  components: {
    VLoader,
  },
  data() {
    return {
      tableTooltip: false,
    };
  },
  created() {
    if (sessionStorage.hidePillTooltip == "true") {
      this.tableTooltip = false;
    } else {
      this.tableTooltip = true;
    }
  },
  computed: {
    ...mapGetters("order", [
      "basicData",
      "priceList",
      "deliveryTypes",
      "freeContingent",
      "orderListOnProduction",
      "orderListOnHold",
      "onProductionElements",
      "onHold",
      "formatPrice",
    ]),
    netPrice() {
      return this.onProductionElements.reduce((price, p) => price + p.canisters * parseFloat(p.netPrice), 0);
    },
    vatPrice() {
      return (this.netPrice * parseFloat(this.basicData.vatTaxRate)) / 100;
    },
  },
  methods: {
    printEstimatedDate: DateUtils.printEstimatedDate,
    formatDate(value, isUS = false) {
      const date = moment(value);

      return isUS ? date.format("MM/DD/YYYY") : date.format("DD.MM.YYYY");
    },
    hideTooltip() {
      this.tableTooltip = false;
      sessionStorage.hidePillTooltip = true;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/variables";
.order-info {
  padding: 51px 71px 31px 75px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e4e4e4;
  position: relative;
  @media all and (max-width: 1200px) {
    padding-left: 30px;
    padding-right: 30px;
  }
  &__table {
    width: 100%;
    text-align: left;
    th {
      font-size: 13px;
      line-height: 14px;
      letter-spacing: 0.464286px;
      color: #999999;
      border-bottom: 1px solid #ececec;
      padding: 0 0 13px 0;
      &:nth-child(1) {
        width: 162px;
      }
      &:nth-child(2) {
        width: 299px;
      }
      &:nth-child(3) {
        width: 280px;
      }
      &:nth-child(4) {
        width: 160px;
      }
    }
    td {
      font-size: 13px;
      padding: 20px 0;
      letter-spacing: 0.5px;
      color: #333333;
      border-bottom: 1px solid #ececec;
    }
    .has-child {
      td {
        border: none;
        padding-bottom: 7px;
      }
    }
    .subtotal {
      td {
        padding: 19px 0 7px 0;
        border: none;
        &[colspan] {
          border: none !important;
        }
      }
      &--last {
        td {
          padding-bottom: 20px;
        }
      }
    }
    .intotal {
      td {
        border: none;
        border-top: 1px solid #ececec;
        &.text-right {
          font-size: 24px;
        }
        &[colspan] {
          border: none !important;
        }
      }
    }
    strong {
      font-weight: bold;
    }
  }
  &__desc {
    display: block;
    text-align: right;
    font-size: 13px;
    line-height: 14px;
    letter-spacing: 0.464286px;
    color: #333333;
  }
}
.text-grey {
  text-decoration-line: line-through;
  color: $color-medium-grey !important;
  font-size: 13px;
}
.text-free {
  margin-right: 10px;
}
.popup-info {
  position: absolute;
  z-index: 10;
  right: -57px;
  top: -34px;
  left: auto;
  bottom: auto;
  background: #fff;
  box-shadow: 0px 12px 34px rgba(153, 153, 153, 0.5);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  width: 510px;
  padding: 31px 40px 29px 33px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  &.open {
    opacity: 1;
    visibility: visible;
  }
  &__text {
    width: calc(100% - 142px);
  }
  &__title {
    font-size: 16px;
    line-height: 17px;
    margin: 10px 0 9px 0;
    color: #000000;
    letter-spacing: 0;
  }
  &__desc {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0.433333px;
    color: #000000;
    strong {
      font-weight: bold;
    }
  }
  &__close {
    position: absolute;
    right: 24px;
    top: 12px;
    cursor: pointer;
    img {
      width: 12px;
    }
  }
}
</style>
